import { ModularSofa, Services } from '@fixtuur/backpack';
import * as Hooks from 'preact/hooks';
import { useUndoState } from '../useUndoState';

export type Modify =
    | {
          action: 'remove';
          position: number;
      }
    | { action: 'replace' | 'insert'; position: number; piece: string };

export const useModularConfiguration = (
    modularTemplate?: Services.ModularTemplates.ModularTemplateWithOptions,
    initialGlobalSelectionProposals?: ModularSofa.PropertySelections,
    initialPieceSelectionProposals?: Array<string | ModularSofa.PropertySelections>,
) => {
    type State = { pieces: Array<string>; globalSelections: ModularSofa.PropertySelections };
    const {
        current: state,
        pushState,
        clear,
        undo,
        redo,
    } = useUndoState<State>({
        pieces: [],
        globalSelections: {},
    });

    const [initialised, setInitialised] = Hooks.useState(false);

    const availableModules = Hooks.useMemo(
        () =>
            initialised && modularTemplate
                ? ModularSofa.getAvailableModulesForGlobalSelections(
                      modularTemplate,
                      state.globalSelections,
                  )
                : {},
        [initialised, modularTemplate, state.globalSelections],
    );

    const globalPropertyOptions = Hooks.useMemo(() => {
        return modularTemplate && ModularSofa.getPropertyOptions(modularTemplate);
    }, [modularTemplate]);

    const initialState = Hooks.useMemo(() => {
        if (!modularTemplate || !initialGlobalSelectionProposals || !globalPropertyOptions)
            return null;

        const initialGlobalSelections = Object.fromEntries(
            Object.entries(initialGlobalSelectionProposals).filter(([k, v]) =>
                (globalPropertyOptions[k] ?? []).includes(v),
            ),
        );

        const initialPieces = initialPieceSelectionProposals?.map(pieceProposal => {
            if (typeof pieceProposal === 'string') return pieceProposal;
            const candidates = Object.values(modularTemplate.modules).flat();
            candidates.sort((a, b) => {
                const pieceProposalCompare =
                    ModularSofa.countEqualProperties(pieceProposal, b.properties) -
                    ModularSofa.countEqualProperties(pieceProposal, a.properties);
                const globalProposalCompare =
                    ModularSofa.countEqualProperties(initialGlobalSelections, b.properties) -
                    ModularSofa.countEqualProperties(initialGlobalSelections, b.properties);

                return pieceProposalCompare !== 0 ? pieceProposalCompare : globalProposalCompare;
            });
            return candidates[0].particleId;
        });

        if (initialPieces) {
            const initialPieceProperties = ModularSofa.getModuleById(
                modularTemplate,
                initialPieces[0],
            )[1].properties;
            for (const k in initialGlobalSelections) {
                if (k in initialPieceProperties) {
                    initialGlobalSelections[k] = initialPieceProperties[k];
                }
            }
        }

        // PIECES
        const candidateModules = ModularSofa.getAvailableModulesForGlobalSelections(
            modularTemplate,
            initialGlobalSelections,
        );

        const pieces = ModularSofa.updateGlobalPropertySelection(
            initialPieces ??
                (candidateModules['middle']?.[0].particleId
                    ? [candidateModules['middle']?.[0].particleId]
                    : []),
            modularTemplate,
            initialGlobalSelections,
        );

        return { pieces, globalSelections: initialGlobalSelections };
    }, [
        modularTemplate,
        globalPropertyOptions,
        initialGlobalSelectionProposals,
        initialPieceSelectionProposals,
    ]);

    const validatePartialBuild = Hooks.useMemo(
        () => (modularTemplate ? ModularSofa.Rules.buildRules(modularTemplate) : () => false),
        [modularTemplate],
    );

    const validateCompleteBuild = Hooks.useMemo(
        () => (modularTemplate ? ModularSofa.Rules.finalisationRules(modularTemplate) : () => []),
        [modularTemplate],
    );

    function createModified(pieces: string[], modification: Modify): string[] {
        const newPieces = pieces.slice();
        switch (modification.action) {
            case 'remove':
                newPieces.splice(modification.position, 1);
                break;
            case 'insert':
                newPieces.splice(modification.position, 0, modification.piece);
                break;
            case 'replace':
                newPieces.splice(modification.position, 1, modification.piece);
                break;
        }
        return newPieces;
    }

    const checkValidPartialBuild = Hooks.useCallback(
        (modification: Modify) => {
            const newPieces = createModified(state.pieces, modification);
            return validatePartialBuild(newPieces);
        },
        [state.pieces, validatePartialBuild],
    );

    const finalValidationErrors = Hooks.useMemo(
        () => (state.pieces.length ? validateCompleteBuild(state.pieces) : []),
        [state.pieces, validateCompleteBuild],
    );

    const modify = Hooks.useCallback(
        (modification: Modify) => {
            const newPieces = createModified(state.pieces, modification);
            if (!validatePartialBuild(newPieces)) {
                return;
            }
            pushState(state => ({ ...state, pieces: newPieces }));
        },
        [state.pieces, validatePartialBuild, pushState],
    );

    const canInsertAt = Hooks.useCallback(
        (position: number) => {
            return Object.values(availableModules)
                .flat()
                .some(module => {
                    const modification: Modify = {
                        action: 'insert',
                        position,
                        piece: module.particleId,
                    };
                    return checkValidPartialBuild(modification);
                });
        },
        [checkValidPartialBuild, availableModules],
    );

    const updateGlobalSelection = Hooks.useCallback(
        (property: string, value: string | number | null) => {
            if (!modularTemplate) return;
            pushState(({ pieces, globalSelections, ...rest }) => {
                const newSelections = { ...globalSelections, [property]: value };
                const newPieces = ModularSofa.updateGlobalPropertySelection(
                    pieces,
                    modularTemplate,
                    newSelections,
                );
                return {
                    ...rest,
                    pieces: newPieces,
                    globalSelections: newSelections,
                };
            });
        },
        [modularTemplate, pushState],
    );

    // Default configuration
    Hooks.useEffect(() => {
        if (initialState !== null) {
            clear(initialState);
            setInitialised(true);
        }
    }, [initialState]);

    return {
        ...state,
        availableModules,
        globalPropertyOptions,
        modify,
        checkValidPartialBuild,
        finalValidationErrors,
        canInsertAt,
        updateGlobalSelection,
        undo,
        redo,
    };
};
